import * as React from "react";
import styled from "styled-components";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import { getNiceAxisTicks } from "../utils/getNiceAxisTicks";
import { TooltipBox, TooltipRow, TooltipDot, TooltipColon } from "./ChartTooltip";

// Recharts clones the tick element with x/y/payload, so the category label can
// be made a drilldown affordance alongside the bar itself.
const ClickableTickText = styled.text`
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ClickableTick = ({ x, y, payload, onSelect }: any) => (
  <ClickableTickText
    x={x}
    y={y}
    dy={4}
    textAnchor="end"
    fontSize={13}
    fill="#666"
    onClick={() => onSelect(payload.value)}
  >
    {payload.value}
  </ClickableTickText>
);

const Caption = styled.p`
  margin: 20px 0px;
  padding-bottom: 10px;
  text-align: center;
  color: #666666;
`;

interface Datum {
  name: string;
  value: number;
}

interface Props {
  data: Datum[];
  color: string;
  tooltipFormatter: (value: number) => string;
  xAxisTitle?: string;
  caption?: string;
  /** Shows a bold value label at the end of each bar. Default true. */
  showValueLabels?: boolean;
  /** Defaults to `tooltipFormatter` when omitted. */
  valueLabelFormatter?: (value: number) => string;
  /** Fixed x-axis tick increment (e.g. 5 for a 0-100% axis). Omit for Recharts' auto "nice" ticks. */
  tickStep?: number;
  rowHeight?: number;
  barThickness?: number;
  chartMargin?: number;
  /** Makes the bars clickable, e.g. to drill into a category. */
  onBarClick?: (datum: Datum) => void;
}

// Prod renders each row at a fixed pixel size regardless of how many rows
// there are (measured ~8px bar / ~9px gap whether there were 13 or 26
// rows), so the chart grows taller with more rows rather than shrinking
// the bars to fit a fixed height.
const ROW_HEIGHT = 20;
const BAR_THICKNESS = 12;
const CHART_MARGIN = 90;
const Y_AXIS_MIN_WIDTH = 120;
const Y_AXIS_MAX_WIDTH = 260;
const Y_AXIS_LABEL_PADDING = 16;

// Rough average glyph width for a 13px sans-serif label, used to size the
// y-axis so long category names don't get clipped by the chart's SVG bounds.
const estimateTextWidth = (text: string, fontSize = 13) =>
  text.length * fontSize * 0.6;

const getYAxisWidth = (names: string[]) => {
  if (!names.length) return Y_AXIS_MIN_WIDTH;
  const longest = Math.max(...names.map((name) => estimateTextWidth(name)));
  return Math.min(
    Y_AXIS_MAX_WIDTH,
    Math.max(Y_AXIS_MIN_WIDTH, longest + Y_AXIS_LABEL_PADDING)
  );
};

interface TooltipContentProps extends TooltipProps<number, string> {
  tooltipFormatter: (value: number) => string;
  color: string;
}

const CustomTooltip: React.FC<TooltipContentProps> = ({
  active,
  payload,
  label,
  tooltipFormatter,
  color,
}) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <TooltipBox>
      <div>{label}</div>
      <TooltipRow>
        <TooltipDot dotColor={color} />
        <TooltipColon>:</TooltipColon>
        <strong>{tooltipFormatter(payload[0].value as number)}</strong>
      </TooltipRow>
    </TooltipBox>
  );
};

export const RankingBarChart: React.FC<Props> = ({
  data,
  color,
  tooltipFormatter,
  xAxisTitle,
  caption,
  showValueLabels = true,
  valueLabelFormatter = tooltipFormatter,
  tickStep,
  rowHeight = ROW_HEIGHT,
  barThickness = BAR_THICKNESS,
  chartMargin = CHART_MARGIN,
  onBarClick,
}) => {
  const selectByName = (name: string) => {
    const datum = data.find((d) => d.name === name);
    if (datum) onBarClick?.(datum);
  };

  const xAxisTicks = getNiceAxisTicks(data.map((d) => d.value), 5, tickStep);
  const yAxisWidth = getYAxisWidth(data.map((d) => d.name));
  const chartHeight = data.length * rowHeight + chartMargin;

  return (
    <div>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          data={data}
          layout="vertical"
          // Handled here rather than on <Bar>: the Tooltip cursor rectangle
          // renders above the bars and swallows their click events.
          onClick={
            onBarClick
              ? (event: any) => {
                  // activeLabel is the category axis value, which is the most
                  // reliable identifier in a vertical layout; fall back to the
                  // payload for safety.
                  const payload = event?.activePayload?.[0]?.payload;
                  const name = event?.activeLabel ?? payload?.name;
                  if (name === undefined || name === null) return;
                  onBarClick(payload ?? { name, value: 0 });
                }
              : undefined
          }
          margin={{
            top: 10,
            right: showValueLabels ? 56 : 20,
            left: 10,
            bottom: xAxisTitle ? 20 : 5,
          }}
        >
          <CartesianGrid horizontal={false} stroke="#e6e6e6" />
          <XAxis
            type="number"
            domain={[0, xAxisTicks[xAxisTicks.length - 1]]}
            ticks={xAxisTicks}
            tick={{ fontSize: 13 }}
            label={
              xAxisTitle
                ? {
                    value: xAxisTitle,
                    position: "insideBottom",
                    offset: -5,
                    style: { fontSize: 13, textAnchor: "middle" },
                  }
                : undefined
            }
          />
          <YAxis
            type="category"
            dataKey="name"
            width={yAxisWidth}
            tick={
              onBarClick ? (
                <ClickableTick onSelect={selectByName} />
              ) : (
                { fontSize: 13 }
              )
            }
            interval={0}
            tickLine={false}
          />
          <Tooltip
            content={
              <CustomTooltip tooltipFormatter={tooltipFormatter} color={color} />
            }
            cursor={{ fill: "rgba(0,0,0,0.05)" }}
          />
          <Bar
            dataKey="value"
            fill={color}
            barSize={barThickness}
            cursor={onBarClick ? "pointer" : undefined}
          >
            {showValueLabels && (
              <LabelList
                dataKey="value"
                position="right"
                formatter={valueLabelFormatter}
                fill="#000"
                style={{ fontSize: 13, fontWeight: "bold" }}
              />
            )}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {caption && <Caption>{caption}</Caption>}
    </div>
  );
};
